import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { ukDayTime, ukDayYear, ukWeekdayTime } from "@/lib/time";
import { daysSince, deskState, gbp, nowOf, phaseOf } from "@/lib/stages";
import { decisionDue } from "@/lib/caseflow";
import {
  Bar, Btn, CellMain, CellSub, Chips, DateCell, DeskSupport, Divider, Empty, Kv, LinkRow, Money, Note, PageBody, PageHead, RailCard, SearchBox, Sec, StatStrip, Tb, Td, Th, openRow,
} from "@/components/portal/crm/kit";
import { StagePicker } from "@/components/portal/sys/StagePicker";
import { NowPicker } from "@/components/portal/sys/NowPicker";
import { StageNudge } from "@/components/portal/crm/StageNudge";
import { CallCheck } from "@/components/portal/crm/CallCheck";
import { LiveLine } from "@/components/portal/crm/LiveLine";
import { FastTrackTag } from "@/components/portal/crm/FastTrackTag";
import { CaseStamp } from "@/components/portal/crm/CaseStamp";
import { AdminPreviewBanner } from "@/pages/introducer/Portal";
import { AddedFilter } from "@/components/portal/crm/AddedFilter";
import { inRange, matchesCase, newestFirst } from "@/lib/caseList";
import { DocsProgress } from "@/components/portal/crm/DocsProgress";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "sent", label: "With lenders", tone: "ink" },
  { key: "in_review", label: "Under assessment", tone: "blue" },
  { key: "awaiting_docs", label: "Awaiting documents", tone: "amber" },
  { key: "preparing_offer", label: "Preparing offer", tone: "teal" },
  { key: "offer_made", label: "Offer issued", tone: "mint" },
  { key: "offer_accepted", label: "Offer accepted", tone: "acc" },
  { key: "completed", label: "Funded", tone: "green" },
  { key: "declined", label: "Declined", tone: "red" },
  { key: "archived", label: "Archived" },
];

const laneKey = (l) => (l.step === "offer_accepted" ? "offer_accepted" : phaseOf(l, l.docs_open) || l.status);

const quarterStart = () => {
  const n = new Date();
  return new Date(Date.UTC(n.getUTCFullYear(), Math.floor(n.getUTCMonth() / 3) * 3, 1)).getTime();
};

export default function Cases() {
  const { leads, me, isAdmin, onStatusPick, onNowClear, reload } = useOutletContext();
  const nav = useNavigate();
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [added, setAdded] = useState({ key: "any" });
  // Archived cases are out of the live list by default and fetched only when asked for.
  const [archived, setArchived] = useState(null);

  useEffect(() => {
    if (filter !== "archived" || archived !== null) return;
    api.get("/lender/leads", { params: { archived: 1 } })
      .then((r) => setArchived(r.data.items || [])).catch(() => setArchived([]));
  }, [filter, archived]);

  const sla = me?.turnaround_days || 3;

  // Newest to the desk first — the day it landed with you, not the day someone last touched it.
  const counts = useMemo(() => {
    const live = leads.filter((l) => inRange(l.sent_at || l.created_at, added));
    const c = { all: live.length, archived: (archived || []).filter((l) => inRange(l.sent_at || l.created_at, added)).length };
    FILTERS.forEach((f) => {
      if (!["all", "archived"].includes(f.key)) c[f.key] = live.filter((l) => laneKey(l) === f.key).length;
    });
    return c;
  }, [leads, archived, added]);

  const rows = useMemo(() => {
    const source = filter === "archived" ? (archived || []) : leads;
    return newestFirst(source, "sent_at").filter((l) => {
      if (!inRange(l.sent_at || l.created_at, added)) return false;
      if (filter === "paused") { if (l.sla?.state !== "waiting_client") return false; }
      else if (!["all", "archived"].includes(filter) && laneKey(l) !== filter) return false;
      return matchesCase(l, q);
    });
  }, [leads, archived, filter, q, added]);

  const stats = useMemo(() => {
    const qs = quarterStart();
    const newToday = leads.filter((l) => l.status === "sent" && daysSince(l.sent_at) === 0).length;
    const waiting = leads.filter((l) => l.status === "sent");
    const overdue = waiting.filter((l) => decisionDue(l, sla)?.state === "late").length;
    const fundedQ = leads.filter((l) => l.status === "completed" && new Date(l.status_updated_at || l.sent_at || 0).getTime() >= qs);
    const drawn = fundedQ.reduce((s, l) => s + (l.amount_gbp || 0), 0);
    const decided = leads.filter((l) => l.status !== "sent" && l.sent_at && l.status_updated_at);
    const avgDays = decided.length
      ? decided.reduce((s, l) => s + Math.max(0, (new Date(l.status_updated_at) - new Date(l.sent_at)) / 86400000), 0) / decided.length
      : null;
    const noReason = leads.filter((l) => l.status === "declined" && !l.decline_reason).length;
    const funded = leads.filter((l) => l.status === "completed").length;
    return { newToday, waiting: waiting.length, overdue, fundedQ: fundedQ.length, drawn, avgDays, noReason, funded };
  }, [leads, sla]);

  const needs = useMemo(() => {
    const out = [];
    if (stats.overdue) out.push({ key: "late", line: `${stats.overdue} decision${stats.overdue === 1 ? "" : "s"} due now`, filter: "sent" });
    const today = leads.filter((l) => decisionDue(l, sla)?.state === "today").length;
    if (today) out.push({ key: "today", line: `${today} due before midnight`, filter: "sent" });
    const docs = leads.filter((l) => l.docs_open > 0).length;
    if (docs) out.push({ key: "docs", line: `${docs} waiting on paperwork`, filter: "in_review" });
    const paused = leads.filter((l) => l.sla?.state === "waiting_client").length;
    if (paused) out.push({ key: "paused", line: `${paused} waiting on the client (clock${paused === 1 ? "" : "s"} paused)`, filter: "paused" });
    return out;
  }, [leads, sla, stats.overdue]);

  const conversion = leads.length ? Math.round((stats.funded / leads.length) * 100) : 0;

  const rail = (
    <>
      <DeskSupport body="Brings cases to your desk and chases anything you are waiting on." />

      <RailCard title="Needs you today" testId="lender-needs-you">
        {needs.length === 0
          ? <div style={{ fontSize: 13, color: "var(--mut)" }}>Nothing is waiting on your desk. Everything we sent has an answer.</div>
          : needs.map((n) => (
            <LinkRow key={n.key} testId={`lender-needs-${n.key}`} onClick={() => setFilter(n.filter)}
                     label={n.key === "late" ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><i className="attn-dot" /><b style={{ color: "var(--attn-tx)" }}>{n.line}</b></span> : n.line} />
          ))}
      </RailCard>

      <RailCard title="Your criteria, in short" testId="lender-criteria-summary">
        <Kv rows={[
          ["Facility range", `${gbp(me?.min_deal_size_gbp)} – ${me?.max_deal_size_gbp ? gbp(me.max_deal_size_gbp) : "no cap"}`],
          ["Finance types", String((me?.finance_types || []).length || "all")],
          ["Sectors declined", String((me?.industry_sectors || []).length || 0)],
          ["Decision SLA", `${sla} day${sla === 1 ? "" : "s"}`],
        ]} />
        <Divider style={{ margin: "18px 0" }} />
        <LinkRow label="Edit criteria" onClick={() => nav("/lender/criteria")} />
        <LinkRow label="Completion fees" onClick={() => nav("/lender/fees")} />
      </RailCard>

      <Note>You only ever see your own lane on a case. Who else is holding it, and who introduced it, stays with Connexis.</Note>
    </>
  );

  return (
    <>
      {isAdmin && <AdminPreviewBanner what="lender desk" />}

      {me?.agreement?.status !== "signed" && (
        <div className="warn" data-testid="agreement-callout" style={{ marginBottom: 22 }}>
          <span>
            <b>Your panel agreement is waiting on your signature.</b> Twenty seconds in the portal — type your name, done.{" "}
            <Link to="/lender/documents" style={{ fontWeight: 800, textDecoration: "underline" }}>Read it and sign</Link>
          </span>
        </div>
      )}

      <PageHead
        eyebrow={me?.company_name}
        title="Cases on your desk"
        lede={`${leads.length} case${leads.length === 1 ? "" : "s"} introduced to you. Change a stage straight from the row — Connexis and the introducer are updated the moment you do.`}
        testId="lender-cases-head"
        actions={
          <>
            <Btn as={Link} to="/lender/criteria">Set your criteria</Btn>
            <Btn kind="primary" onClick={() => setFilter("sent")} testId="lender-review-oldest">Review oldest first</Btn>
          </>
        }
      />

      <PageBody rail={rail}>
        <div data-testid="lender-stats">
          <StatStrip
            items={[
              { k: "New today", v: String(stats.newToday), h: "Arrived in the last 24 hours", testId: "lender-stat-new" },
              { k: "Awaiting your decision", v: String(stats.waiting),
                h: stats.overdue ? <><b style={{ color: "var(--attn-tx)" }}>{stats.overdue} due now</b> against your {sla}-day SLA</> : `All inside your ${sla}-day SLA`,
                testId: "lender-stat-waiting" },
              { k: "Funded this quarter", v: String(stats.fundedQ), h: <><span className="num">{gbp(stats.drawn, { compact: true })}</span> drawn</>, testId: "lender-stat-funded" },
              { k: "Average decision", v: stats.avgDays == null ? "—" : stats.avgDays.toFixed(1), unit: stats.avgDays == null ? null : "days",
                h: "Measured from the moment we sent it", testId: "lender-stat-speed" },
            ]}
          />
        </div>

        <div className="sec" style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
          <SearchBox value={q} onChange={setQ} placeholder="Search name, business, email, phone or reference" testId="lender-search" />
          <AddedFilter value={added} onChange={setAdded} label="Arrived" testId="lender-added" />
          <div style={{ marginLeft: "auto", maxWidth: "100%", minWidth: 0, overflowX: "auto" }} data-testid="lender-stage-tabs">
            <Chips items={FILTERS.map((f) => ({ ...f, count: counts[f.key] || 0 }))} value={filter} onChange={setFilter} testPrefix="lender-filter" />
          </div>
        </div>

        {filter === "paused" && (
          <div data-testid="lender-paused-filter-hint" style={{ marginTop: 12, fontSize: 13, color: "var(--mut)" }}>
            Showing cases waiting on the client — their clocks are paused.{" "}
            <button type="button" onClick={() => setFilter("all")} data-testid="lender-paused-filter-clear"
                    style={{ fontWeight: 700, textDecoration: "underline", color: "var(--ink-3)" }}>Show all</button>
          </div>
        )}

        <div className="card" style={{ marginTop: 16, overflow: "visible" }} data-testid="lender-dealflow">
          {rows.length === 0 ? (
            <Empty testId="lender-cases-empty">Nothing in this view. New applications routed to you appear here.</Empty>
          ) : (
            <Tb
              testId="lender-leads-table"
              head={<><Th>Business</Th><Th>Facility</Th><Th>Received</Th><Th>Stage</Th><Th className="r">Decision due</Th></>}
            >
              {rows.map((l) => {
                const st = deskState(l, sla);
                const due = decisionDue(l, sla);
                const flag = due?.state === "paused" ? "" : due?.state === "late" ? "flag" : due?.state === "today" ? "flag-amber" : l.docs_open > 0 ? "flag-amber" : "";
                return (
                  <tr key={l.id} className={flag} style={{ cursor: "pointer" }} onClick={openRow(nav, `/lender/cases/${l.id}`)}
                      data-testid={`lender-lead-row-${l.id}`}>
                    <td className="lead">
                      <CellMain>{l.business_name || l.applicant_name}</CellMain>
                      <CellSub>{[l.industry_sector, l.company_city].filter(Boolean).join(", ") || l.applicant_name}</CellSub>
                      {l.introduced_by && <CellSub data-testid={`lender-lead-introduced-by-${l.id}`}>Introduced by {l.introduced_by}</CellSub>}
                      <div><CaseStamp at={l.created_at} testId={`lender-lead-stamp-${l.id}`} /></div>
                      {l.fast_track && <div style={{ marginTop: 8 }}><FastTrackTag lead={l} testId={`lender-lead-fast-track-${l.id}`} /></div>}
                    </td>
                    <Td><Money v={l.amount_gbp} sub={`${l.finance_type}${l.term_months ? `, ${l.term_months} months` : ""}`} /></Td>
                    <Td>
                      <DateCell>{ukDayTime(l.sent_at, "—")}</DateCell>
                      <CellSub>{l.docs_open > 0 ? `${l.docs_open} document${l.docs_open === 1 ? "" : "s"} outstanding` : "Introduced by Connexis"}</CellSub>
                    </Td>
                    <Td>
                      <StagePicker value={l.step || l.status} state={st} docs={l.docs_open || 0} disabled={isAdmin || l.status === "declined"}
                                   onPick={(v) => onStatusPick(l.id, v)} testId={`lender-lead-status-${l.id}`} />
                      {st.key === "awaiting_docs" && <div><DocsProgress progress={l.docs_progress} variant="mini" testId={`lender-lead-docs-progress-${l.id}`} /></div>}
                      {!l.withdrawn && (
                        <NowPicker now={nowOf(l)} status={l.status} disabled={isAdmin} onPick={(k) => onStatusPick(l.id, k)}
                                   onClear={() => onNowClear(l.id)} testId={`lender-lead-now-${l.id}`} />
                      )}
                      <LiveLine move={l.move} testId={`lender-lead-live-${l.id}`} />
                      {!isAdmin && <CallCheck row={l} client={l.applicant_name} compact onPick={(k) => onStatusPick(l.id, k)} onClear={() => onNowClear(l.id)}
                                              testId={`lender-lead-callcheck-${l.id}`} />}
                      {!isAdmin && <StageNudge leadId={l.id} suggest={l.suggest} onPick={onStatusPick} onDone={reload}
                                               testId={`lender-lead-nudge-${l.id}`} />}
                    </Td>
                    <Td className="r">
                      {l.status === "declined" ? (
                        l.withdrawn ? (
                          <span data-testid={`lender-lead-withdrawn-${l.id}`}>
                            <Money raw="Withdrawn" tone="muted"
                                   sub={l.withdrawn_reason ? `We took it back — ${l.withdrawn_reason.toLowerCase()}` : "Taken back by Connexis"} />
                          </span>
                        ) : (
                          <Money raw="Closed" tone="muted" sub={l.decline_reason || "Reason sent to Connexis"} />
                        )
                      ) : l.status === "completed" ? (
                        <Money raw="Complete" tone="muted" sub="Fee invoiced" />
                      ) : l.step === "offer_accepted" ? (
                        <Money raw="Accepted" tone="green" sub="Arranging the funds" />
                      ) : l.step === "offer_declined" ? (
                        <Money raw="Turned down" tone="muted" sub="Still Offer issued · send new terms, or pass" />
                      ) : l.status === "offer_made" ? (
                        <Money raw={ukDayYear(l.offer?.expires_on || l.status_updated_at, "—")} sub={l.offer?.expires_on ? "Offer expires" : "Terms with the client"} />
                      ) : due?.state === "paused" ? (
                        <Money raw="Paused" tone="muted" testId={`lender-lead-paused-${l.id}`}
                               sub={`${due.label}${due.resumes ? ` · resumes ${ukWeekdayTime(due.resumes)}` : ""}`} />
                      ) : due?.state === "late" ? (
                        <Money raw={due.label} tone="attn" testId={`lender-lead-due-${l.id}`}
                               sub={`${due.note ? `${due.note} · ` : ""}was due ${ukDayTime(new Date(due.due).toISOString())}`} />
                      ) : due?.state === "today" ? (
                        <>
                          <div className="money" style={{ color: "#8A5200" }}>{ukDayTime(new Date(due.due).toISOString())}</div>
                          <div className="money-sub">{due.label}</div>
                        </>
                      ) : (
                        <Money raw={due ? ukDayYear(new Date(due.due).toISOString()) : "—"} sub={due?.label} />
                      )}
                    </Td>
                  </tr>
                );
              })}
            </Tb>
          )}
          <div className="card-f">
            <span>Showing {rows.length} of {leads.length}. A soft blue edge marks a decision due now, amber means it is due today.</span>
            <span style={{ fontWeight: 700, color: "var(--ink-3)" }}>{ukDayTime(new Date().toISOString())} UK</span>
          </div>
        </div>

        <Sec title="Your desk this quarter" sub="How your own numbers read. Visible to you only.">
          <div className="card">
            <div className="card-b desk-thirds" data-testid="lender-desk-quarter">
              <div>
                <div className="rail-t">Conversion</div>
                <div className="money" style={{ fontSize: 26 }}>{conversion}%</div>
                <Bar pct={conversion} style={{ marginTop: 14 }} />
                <div className="ladder-lab"><span>{stats.funded} funded</span><span>{leads.length} introduced</span></div>
              </div>
              <div>
                <div className="rail-t">Speed to a decision</div>
                <div className="money" style={{ fontSize: 26 }}>{stats.avgDays == null ? "—" : `${stats.avgDays.toFixed(1)} days`}</div>
                <Bar pct={stats.avgDays == null ? 0 : Math.max(6, 100 - (stats.avgDays / Math.max(1, sla)) * 100)} tone="var(--green)" style={{ marginTop: 14 }} />
                <div className="ladder-lab"><span>Your SLA is {sla} day{sla === 1 ? "" : "s"}</span><span /></div>
              </div>
              <div>
                <div className="rail-t">Declined without a reason</div>
                <div className="money" style={{ fontSize: 26 }}>{stats.noReason}</div>
                <Bar pct={leads.length ? (stats.noReason / leads.length) * 100 : 0} tone="var(--red)" style={{ marginTop: 14 }} />
                <div className="ladder-lab"><span>Reasons keep you in the flow</span><span /></div>
              </div>
            </div>
          </div>
        </Sec>
      </PageBody>
    </>
  );
}
